/**
 * Small utility helpers that don't warrant their own module.
 *
 * Keep this file tiny — anything non-trivial goes into its own module.
 */

/**
 * Format a CZK amount using `Intl.NumberFormat` for the given locale.
 *
 * Pricing is stored in CZK; this helper is the display layer. Currency
 * conversion (EUR / USD via ČNB) lives elsewhere.
 */
export function formatMoney(
  amountCzk: number,
  currency: string = "CZK",
  locale: string = "cs-CZ",
  fxRateToCzk: number = 1,
  /** KAN-2189 — cena za kus vždy na dvě místa (viz ``formatUnitPrice``). */
  unitPrice: boolean = false,
): string {
  const normalized = (currency || "CZK").toUpperCase();
  const safeRate =
    Number.isFinite(fxRateToCzk) && fxRateToCzk > 0 ? fxRateToCzk : 1;
  const displayAmount =
    normalized === "CZK" ? amountCzk : amountCzk / safeRate;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: normalized,
    maximumFractionDigits: unitPrice || normalized !== "CZK" ? 2 : 0,
    ...(unitPrice ? { minimumFractionDigits: 2 } : {}),
  }).format(displayAmount);
}

/**
 * Cena za kus — vždy na dvě desetinná místa (KAN-1900).
 *
 * ``formatMoney`` u korun desetiny zahazuje, což je u součtů v pořádku,
 * ale u ceny za kus lže: „20 × 101 Kč = 2 016 Kč" se nesečte, protože kus
 * stojí 100,80. ``amount`` je už v měně ``currency`` (žádný kurz).
 */
export function formatUnitPrice(
  amount: number,
  currency: string,
  locale: string = "cs-CZ",
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatCzk(amount: number, locale: string = "cs-CZ"): string {
  return formatMoney(amount, "CZK", locale, 1);
}

/**
 * BE serializuje Decimal jako string ("21.00", "19.00"). Pro display
 * trimni zbytečné nuly: "21" pro celé sazby, "12.5" pro půlsazby.
 * Žádné zaokrouhlení.
 */
export function formatVatRate(rate: string | number): string {
  const num = typeof rate === "number" ? rate : Number(rate);
  if (!Number.isFinite(num)) return String(rate);
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(2).replace(/\.?0+$/, "");
}

/**
 * Decimal ze zálohy pro člověka: bez koncových nul, s desetinnou
 * čárkou podle jazyka (KAN-1497).
 *
 * BE serializuje Decimal se svým DB měřítkem, takže „dva kusy" přijdou
 * jako ``"2.0000"`` a hodinová sazba jako ``"350.0000"``. Vypsané tak,
 * jak dorazily, to vypadá na obrazovce dílny jako přesnost, kterou to
 * nemá — a desetinná tečka v českém rozhraní jako překlep.
 *
 * ``maximumFractionDigits`` je 4 schválně: víc než tolik BE neposílá
 * a zaokrouhlovat se tu nesmí. Nečíselný vstup se vrací beze změny —
 * zobrazit „NaN" místo hodnoty, které nerozumíme, je horší.
 *
 * Tohle NENÍ pro editační pole: tam se drží hodnota, jak přišla, aby
 * uložení vrátilo totéž číslo.
 */
export function formatDecimal(
  value: string | number | null | undefined,
  locale: string = "cs-CZ",
  /** Pevný počet desetinných míst (KAN-1644: „38,5 cm³", „řez 1,20 m") —
   *  místo ``toFixed``, které píše tečku v každém jazyce. */
  fractionDigits?: number,
): string {
  if (value == null || value === "") return "";
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return String(value);
  const digits =
    fractionDigits === undefined
      ? { maximumFractionDigits: 4 }
      : { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits };
  return new Intl.NumberFormat(locale, digits).format(num);
}

/**
 * Concatenate conditional class names. Subset of `clsx` — kept inline so
 * we don't pull in a dep for three lines.
 */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}
